import { useState } from "react";
import { useNavigate } from "react-router";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { Chip, ChipRemoveEvent } from "primereact/chip";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

import { ActivityData, EvaluateRequestStatus } from "@/api/activities";
import { AssigneeOption } from "@/hooks/useActivityAssignments";

import S from "./ActivityRequestCard.module.scss";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StatusConfig {
  label: string;
  severity: "success" | "warning" | "danger" | "info";
  icon: string;
}

interface ActivityRequestCardProps {
  activity: ActivityData;
  admins: AssigneeOption[];
  assignedAdminIds: string[];
  onAssignmentChange: (activityId: number, newAdminIds: string[]) => void;
  requestType?: "mentor" | "evaluate";
}

// ---------------------------------------------------------------------------
// Status config maps (module-level — no deps)
// ---------------------------------------------------------------------------

const EVALUATE_STATUS_MAP: Record<EvaluateRequestStatus, StatusConfig> = {
  submitted: { label: "Submitted", severity: "warning", icon: "pi pi-send" },
  in_progress: { label: "In Progress", severity: "info", icon: "pi pi-clock" },
  failed: { label: "Failed", severity: "danger", icon: "pi pi-times-circle" },
  passed: { label: "Completed", severity: "success", icon: "pi pi-check-circle" },
  completed: { label: "Completed", severity: "success", icon: "pi pi-check-circle" },
  needs_more_information: { label: "Needs Info", severity: "info", icon: "pi pi-info-circle" },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ActivityRequestCard = ({
  activity,
  admins,
  assignedAdminIds,
  onAssignmentChange,
  requestType = "mentor",
}: ActivityRequestCardProps) => {
  const navigate = useNavigate();
  const [showAssignDialog, setShowAssignDialog] = useState(false);

  const handleAssignmentChange = (e: MultiSelectChangeEvent) => {
    setShowAssignDialog(false);
    onAssignmentChange(activity.id, e.value as string[]);
  };

  const handleRemoveAdmin = (adminId: string) => {
    onAssignmentChange(
      activity.id,
      assignedAdminIds.filter(id => id !== adminId)
    );
  };

  const getAssignedAdmins = () =>
    assignedAdminIds
      .map(id => admins.find(a => a.value === id))
      .filter(Boolean) as AssigneeOption[];

  const getStatusConfig = (): StatusConfig => {
    if (requestType === "evaluate") {
      return (
        EVALUATE_STATUS_MAP[activity.requestStatus as EvaluateRequestStatus] ??
        EVALUATE_STATUS_MAP.submitted
      );
    }
    if (activity.requestStatus === "closed") {
      return { label: "Closed", severity: "success", icon: "pi pi-check-circle" };
    }
    return { label: "Open", severity: "warning", icon: "pi pi-exclamation-circle" };
  };

  const statusConfig = getStatusConfig();
  const baseRoute =
    requestType === "evaluate"
      ? AdminAppRoutes.routes.champions.routes.evaluateRequests.route
      : AdminAppRoutes.routes.champions.routes.mentorRequests.route;
  const detailPath = `${baseRoute}/${activity.id}`;

  const header = (
    <div className={S.cardHeader}>
      <Tag value={statusConfig.label} severity={statusConfig.severity} icon={statusConfig.icon} />
      {activity.userName && (
        <div className="text-right">
          <div className="body-sm">
            <i className="pi pi-user mr-1" />
            {activity.userName}
          </div>
          <div className="body-xs text-muted">{new Date(activity.createdAt).toLocaleString()}</div>
        </div>
      )}
    </div>
  );

  const footer = (
    <Button
      label="View Details"
      icon="pi pi-arrow-right"
      outlined
      size="small"
      onClick={() => navigate(detailPath)}
    />
  );

  return (
    <Card
      className={S.activityCard}
      header={header}
      footer={footer}
      pt={{
        body: { className: "h-full flex flex-col" },
        content: { className: "flex flex-col flex-1 p-0" },
        footer: { className: S.cardFooter },
      }}
    >
      <h3 className="atlas-h5 mb-2">{activity.text}</h3>
      {activity.description && (
        <p className="mb-2 body-md text-color" style={{ whiteSpace: "pre-wrap" }}>
          {activity.description}
        </p>
      )}
      <div className="flex-1"></div>
      <div className="body-sm text-muted mb-2 pt-2">
        <div className="flex flex-wrap gap-2 align-items-center">
          {getAssignedAdmins().map(admin => (
            <Chip
              key={admin.value}
              label={admin.label}
              removable
              onRemove={(_e: ChipRemoveEvent) => {
                handleRemoveAdmin(admin.value);
                return true;
              }}
              className="body-xs"
            />
          ))}
          <Button
            icon="pi pi-plus"
            label="Assign Admin"
            outlined
            rounded
            size="small"
            severity="secondary"
            className="body-xs"
            onClick={() => setShowAssignDialog(true)}
          />
        </div>
      </div>
      <Dialog
        header="Assign Admins"
        visible={showAssignDialog}
        onHide={() => setShowAssignDialog(false)}
        style={{ width: "450px" }}
      >
        <div className="flex flex-col gap-3">
          <label htmlFor={`assign-dialog-${activity.id}`} className="font-semibold">
            Select Admins
          </label>
          <MultiSelect
            id={`assign-dialog-${activity.id}`}
            value={assignedAdminIds}
            onChange={handleAssignmentChange}
            options={admins}
            optionLabel="label"
            optionValue="value"
            placeholder="Select admins"
            className="w-full"
            display="chip"
            filter
            showSelectAll={false}
          />
        </div>
      </Dialog>
    </Card>
  );
};
